/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form } from 'antd';

type OTPProps = {
  form: any;
  errorProp?: string | null;
  onResend?: () => Promise<any> | void;
  resetSignal?: number | string;
  durationSec?: number;
  phoneNumber?: string | null;
};

const DIGITS = 6;
const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

function cn(...cls: Array<string | false | undefined | null>) {
  return cls.filter(Boolean).join(' ');
}

export default function OTP({
  form,
  errorProp,
  onResend,
  resetSignal,
  durationSec = 90,
  phoneNumber,
}: OTPProps) {
  const [timeLeft, setTimeLeft] = useState<number>(durationSec);
  const [resending, setResending] = useState(false);
  const [digits, setDigits] = useState<string[]>(Array(DIGITS).fill(''));

  const otp = useMemo(() => digits.join(''), [digits]);

  const intervalRef = useRef<number | null>(null);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    setTimeLeft(durationSec);

    const id = window.setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearTimer();
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    intervalRef.current = id;
  }, [clearTimer, durationSec]);

  // Start on mount; clean up on unmount
  useEffect(() => {
    startTimer();
    return () => clearTimer();
  }, [startTimer, clearTimer]);

  // Restart when parent signals a resend externally via resetSignal
  useEffect(() => {
    if (resetSignal !== undefined) startTimer();
  }, [resetSignal, startTimer]);

  const canResend = timeLeft === 0 && !resending;

  const handleResend = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (!canResend) return;

      try {
        setResending(true);
        await onResend?.();
        // If resetSignal is not controlled externally, restart timer here
        if (resetSignal === undefined) startTimer();
        // Clear digit state and form value so user enters the fresh code
        setDigits(Array(DIGITS).fill(''));
        form?.setFieldsValue?.({ otp_code: '' });
        inputRefs.current[0]?.focus?.();
      } finally {
        setResending(false);
      }
    },
    [canResend, form, onResend, resetSignal, startTimer],
  );

  const minutes = Math.floor(timeLeft / 60);
  const seconds = pad2(timeLeft % 60);
  const isExpiringSoon = timeLeft <= 10 && timeLeft > 0;

  const expiryText = useMemo(() => {
    if (timeLeft === 0) return 'The OTP has expired';
    return `Expires in ${minutes}:${seconds}`;
  }, [timeLeft, minutes, seconds]);

  const expiryColor =
    timeLeft === 0
      ? 'text-red-600'
      : isExpiringSoon
        ? 'text-amber-600'
        : 'text-[#111827]';

  useEffect(() => {
    inputRefs.current[0]?.focus?.();
  }, []);

  const focusIndex = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(DIGITS - 1, idx));
    inputRefs.current[clamped]?.focus?.();
  }, []);

  const setDigit = useCallback((idx: number, val: string) => {
    setDigits((prev) => {
      if (prev[idx] === val) return prev;
      const next = prev.slice();
      next[idx] = val;
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setDigits(Array(DIGITS).fill(''));
    focusIndex(0);
    form?.setFieldsValue?.({ otp_code: '' });
  }, [focusIndex, form]);

  const setManyFrom = useCallback(
    (startIdx: number, incoming: string) => {
      const cleaned = incoming.replace(/\D/g, '').slice(0, DIGITS - startIdx);
      if (!cleaned) return;

      setDigits((prev) => {
        const next = prev.slice();
        for (let i = 0; i < cleaned.length; i++) {
          next[startIdx + i] = cleaned[i];
        }
        return next;
      });

      const last = Math.min(startIdx + cleaned.length - 1, DIGITS - 1);
      focusIndex(last);
    },
    [focusIndex],
  );

  // Sync when code is complete
  useEffect(() => {
    const v = otp.replace(/\D/g, '');
    if (v.length === DIGITS) {
      form?.setFieldsValue?.({ otp_code: v });
      const errs = form?.getFieldError?.('otp_code') || [];
      if (errs.length) form?.setFields?.([{ name: 'otp_code', errors: [] }]);
    }
  }, [otp, form]);

  // Sync on blur (partial state survives submit validation)
  const handleBlurSync = useCallback(() => {
    form?.setFieldsValue?.({ otp_code: otp });
  }, [form, otp]);

  const fillFromFullCode = useCallback(
    (code: string) => {
      const cleaned = code.replace(/\D/g, '').slice(0, DIGITS);
      if (!cleaned) return;

      const nextDigits = Array(DIGITS)
        .fill('')
        .map((_, i) => cleaned[i] ?? '');

      setDigits(nextDigits);
      focusIndex(Math.min(cleaned.length - 1, DIGITS - 1));

      if (cleaned.length === DIGITS) {
        form?.setFieldsValue?.({ otp_code: cleaned });
        const errs = form?.getFieldError?.('otp_code') || [];
        if (errs.length) form?.setFields?.([{ name: 'otp_code', errors: [] }]);
      }
    },
    [focusIndex, form],
  );

  const handleContainerPaste = useCallback(
    (e: React.ClipboardEvent) => {
      const text = e.clipboardData.getData('text') || '';
      const cleaned = text.replace(/\D/g, '');
      if (!cleaned) return;
      e.preventDefault();
      fillFromFullCode(cleaned);
    },
    [fillFromFullCode],
  );

  /* ─── iOS/Android autofill ─────────────────────────────────────────────── */

  const handleFirstInputAutoFill = useCallback(
    (raw: string) => {
      const cleaned = raw.replace(/\D/g, '');
      if (cleaned.length >= DIGITS) {
        fillFromFullCode(cleaned);
        focusIndex(DIGITS - 1);
        return true;
      }
      return false;
    },
    [fillFromFullCode, focusIndex],
  );

  const handleChange = useCallback(
    (idx: number, raw: string) => {
      const val = (raw || '').replace(/\s+/g, '');
      if (!val) {
        setDigit(idx, '');
        return;
      }
      // Multi-char (spread paste through onChange)
      if (val.length > 1) {
        setManyFrom(idx, val);
        return;
      }
      const digit = val.replace(/\D/g, '');
      if (!digit) return;
      setDigit(idx, digit);
      if (idx < DIGITS - 1) focusIndex(idx + 1);
    },
    [focusIndex, setDigit, setManyFrom],
  );

  const submitNow = useCallback(() => {
    form?.setFieldsValue?.({ otp_code: otp });
    form?.submit?.();
  }, [form, otp]);

  const handleKeyDown = useCallback(
    (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      const key = e.key;

      if (key === 'Enter') {
        e.preventDefault();
        submitNow();
        return;
      }
      if (key === 'ArrowLeft') {
        e.preventDefault();
        focusIndex(idx - 1);
        return;
      }
      if (key === 'ArrowRight') {
        e.preventDefault();
        focusIndex(idx + 1);
        return;
      }
      if (key === 'Home') {
        e.preventDefault();
        focusIndex(0);
        return;
      }
      if (key === 'End') {
        e.preventDefault();
        focusIndex(DIGITS - 1);
        return;
      }
      if (key === 'Delete') {
        e.preventDefault();
        setDigit(idx, '');
        return;
      }

      if (key === 'Backspace') {
        e.preventDefault();
        if (digits[idx]) {
          setDigit(idx, '');
          return;
        }
        if (idx > 0) {
          setDigit(idx - 1, '');
          focusIndex(idx - 1);
        }
        return;
      }
    },
    [digits, focusIndex, setDigit, submitNow],
  );

  return (
    <div className="mx-auto md:mx-0 w-full max-w-[450px] px-4 sm:px-0">
      <div
        className="flex flex-col items-center gap-3 sm:gap-4"
        onPaste={handleContainerPaste}
      >
        {/* Heading */}
        <div className="flex flex-col gap-2 text-start w-full">
          <h1 className="text-[21px] sm:text-[28px] font-semibold text-[#111827]">
            Verify OTP
          </h1>
          <p className="text-[11px] sm:text-[12px] text-[#404040] leading-relaxed">
            Enter the 6-digit code sent to your registered contact to verify
            your identity
          </p>
        </div>

        {/* Countdown + phone number */}
        <div className="flex flex-col gap-1 text-center">
          <div
            className={cn(
              'text-[16px] sm:text-[18px] font-semibold',
              expiryColor,
            )}
          >
            {expiryText}
          </div>
          {phoneNumber ? (
            <div className="text-[13px] sm:text-[14px] font-medium text-[#111827]">
              {phoneNumber}
            </div>
          ) : null}
        </div>

        {/* OTP digit inputs (AntD Form.Item wraps hidden value) */}
        <Form.Item
          name="otp_code"
          label={null}
          preserve
          validateTrigger={[]}
          normalize={(val) =>
            typeof val === 'string' ? val.replace(/\s+/g, '') : val
          }
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Please enter the OTP code',
            },
            {
              validator: (_, value) => {
                const v = String(value || '').replace(/\D/g, '');
                if (!v) return Promise.resolve();
                if (v.length < DIGITS)
                  return Promise.reject(
                    new Error(`Enter the full ${DIGITS}-digit code`),
                  );
                return Promise.resolve();
              },
            },
          ]}
          className="w-full !mb-0"
        >
          <div className="w-full mt-2">
            <div className="mb-3 text-[14px] sm:text-[15px] font-medium text-[#111827]">
              Type your 6 digit security code
            </div>

            <div className="flex items-center justify-between w-full gap-2 sm:gap-4">
              {Array.from({ length: DIGITS }).map((_, idx) => {
                const char = digits[idx] ?? '';
                return (
                  <div
                    key={idx}
                    className={cn(
                      'flex-1 max-w-[52px] sm:max-w-[62px]',
                      'h-[54px] sm:h-[60px] rounded-xl border-2 bg-white',
                      'flex items-center justify-center',
                      'transition',
                      'border-[#16A34A]',
                      'focus-within:ring-2 focus-within:ring-[#86EFAC]',
                    )}
                    onClick={() => focusIndex(idx)}
                  >
                    <input
                      ref={(el) => {
                        inputRefs.current[idx] = el;
                      }}
                      value={char}
                      onChange={(e) => {
                        if (
                          idx === 0 &&
                          handleFirstInputAutoFill(e.target.value)
                        )
                          return;
                        handleChange(idx, e.target.value);
                      }}
                      onKeyDown={(e) => handleKeyDown(idx, e)}
                      onPaste={(e) => {
                        const text = e.clipboardData.getData('text') || '';
                        const cleaned = text.replace(/\D/g, '');
                        if (!cleaned) return;
                        e.preventDefault();
                        setManyFrom(idx, cleaned);
                      }}
                      onBlur={handleBlurSync}
                      inputMode="numeric"
                      autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                      maxLength={1}
                      className="
                        w-full h-full bg-transparent
                        text-center text-[18px] sm:text-[20px] font-semibold text-[#111827]
                        outline-none
                      "
                      aria-label={`OTP digit ${idx + 1}`}
                    />
                  </div>
                );
              })}
            </div>

            {/* Clear code / paste hint */}
            <div className="mt-3 flex items-center justify-between text-[11px]! capitalize">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  clearAll();
                }}
                className="text-[#6B7280] hover:text-[#111827] hover:underline"
              >
                clear code
              </button>
              <div className="text-[#9CA3AF]">
                Tip: you can paste the full code
              </div>
            </div>
          </div>
        </Form.Item>

        {/* ─── Resend row ───────────────────────────────────────────────────
            State 1 — timeLeft > 0   : muted label + live countdown wait hint
            State 2 — timeLeft === 0 : active accent link with hover arrow
            State 3 — resending      : spinner + "Resending…", non-interactive
        ──────────────────────────────────────────────────────────────────── */}
        <div className="w-full flex flex-col items-center gap-2 pt-2">
          <div className="flex flex-wrap justify-center items-center gap-1 text-sm">
            <span className="text-[#6B7280]">Didn&apos;t receive the SMS?</span>

            {/* State 1: timer still running */}
            {timeLeft > 0 && (
              <span className="inline-flex items-center gap-1 text-[#9CA3AF] select-none ">
                Resend in
                <span
                  className={cn(
                    'font-semibold tabular-nums',
                    isExpiringSoon ? 'text-amber-500' : 'text-[#6B7280]',
                  )}
                >
                  {minutes > 0 ? `${minutes}:${seconds}` : `${seconds}s`}
                </span>
              </span>
            )}

            {/* State 2: timer expired, idle */}
            {timeLeft === 0 && !resending && (
              <a
                href="#"
                onClick={handleResend}
                aria-disabled={false}
                className="group inline-flex items-center gap-0.5 font-semibold !text-[var(--color-accent)] hover:opacity-80 hover:underline underline-offset-2 cursor-pointer transition-all select-none"
              >
                <span>Click to resend</span>
                <span className="transition-all duration-200 translate-x-0 opacity-0 group-hover:translate-x-1 group-hover:opacity-100">
                  →
                </span>
              </a>
            )}

            {/* State 3: resend API call in flight */}
            {resending && (
              <span className="inline-flex items-center gap-1.5 text-[#9CA3AF] select-none pointer-events-none">
                <svg
                  className="animate-spin h-3.5 w-3.5 shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                <span>Resending…</span>
              </span>
            )}
          </div>

          {errorProp ? (
            <div className="text-red-500 text-sm text-center">{errorProp}</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
